import { useMemo, useState } from "react";
import { Navigate, useParams, useNavigate } from "react-router-dom";
import { ChevronRight, ShieldAlert } from "lucide-react";
import clsx from "clsx";
import { useAuth } from "../../lib/auth";
import { RESOURCES, SECTIONS, resourceByKey } from "../../lib/admin/configs";
import { canWrite, type ResourceConfig } from "../../lib/admin/resources";
import { SectionTag } from "../../components/ui";
import ResourceTable from "../../components/admin/ResourceTable";
import ResourceForm from "../../components/admin/ResourceForm";
import ResourceDetail from "../../components/admin/ResourceDetail";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Rec = Record<string, any>;

export default function Admin() {
  const { capabilities, allTenants } = useAuth();
  const { resource } = useParams();
  const nav = useNavigate();
  const [viewing, setViewing] = useState<Rec | null>(null);
  const [editing, setEditing] = useState<Rec | null | undefined>(undefined); // undefined=closed, null=create

  const canPlatform = capabilities.includes("platform.manage");
  const canTenant =
    capabilities.includes("config.write") || capabilities.includes("apikey.manage");
  const sections = useMemo(
    () => SECTIONS.filter((s) => (s.key === "platform" ? canPlatform : canTenant)),
    [canPlatform, canTenant]
  );

  const active: ResourceConfig = useMemo(
    () => resourceByKey(resource ?? "") ?? RESOURCES[0],
    [resource]
  );
  const firstVisible = useMemo(
    () => RESOURCES.find((r) => sections.some((s) => s.key === r.section)),
    [sections]
  );

  if (!canPlatform && !canTenant) {
    return (
      <div className="flex h-full min-h-[60vh] flex-col items-center justify-center gap-3 text-center">
        <ShieldAlert size={32} className="text-accent" />
        <h2 className="text-lg font-semibold">Admin portal restricted</h2>
        <p className="max-w-sm text-sm text-slatey">
          The admin portal is available to Super Admins (platform) and Technical Integrators
          (tenant configuration). Your role lands on the Monitor surface.
        </p>
      </div>
    );
  }
  if (!firstVisible) return <Navigate to="/monitor" replace />;
  // No resource, or one the user can't see → land on the first visible resource.
  if (!resource || !sections.some((s) => s.key === active.section)) {
    return <Navigate to={`/admin/${firstVisible.key}`} replace />;
  }

  const writable = canWrite(active, capabilities);

  return (
    <div className="flex h-full min-h-0 gap-4">
      {/* sub-nav (desktop rail) */}
      <aside className="hidden w-56 shrink-0 overflow-y-auto pr-1 lg:block">
        <SectionTag>ADMIN PORTAL</SectionTag>
        <div className="mt-2 space-y-5">
          {sections.map((section) => (
            <div key={section.key}>
              <div className="mb-1.5 px-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-aurora-cyan/80">
                {section.label}
              </div>
              <div className="space-y-3">
                {section.groups.map((group) => {
                  const items = RESOURCES.filter(
                    (r) => r.section === section.key && r.group === group
                  );
                  if (!items.length) return null;
                  return (
                    <div key={group}>
                      <div className="section-tag mb-1 px-1 !tracking-[0.15em]">{group}</div>
                      <div className="space-y-0.5">
                        {items.map((r) => (
                          <button
                            key={r.key}
                            onClick={() => nav(`/admin/${r.key}`)}
                            className={clsx(
                              "flex w-full items-center justify-between rounded-lg px-2.5 py-1.5 text-left text-sm transition",
                              r.key === active.key
                                ? "bg-aurora-cyan/10 text-aurora-cyan"
                                : "text-slate-300 hover:bg-white/5 hover:text-ink"
                            )}
                          >
                            {r.label}
                            {r.key === active.key && <ChevronRight size={13} />}
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </aside>

      {/* main */}
      <div className="min-w-0 flex-1">
        {/* mobile resource picker (rail is hidden below lg) */}
        <div className="mb-3 lg:hidden">
          <select
            value={active.key}
            onChange={(ev) => nav(`/admin/${ev.target.value}`)}
            className="w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm text-ink outline-none focus:border-aurora-cyan/50"
          >
            {sections.map((section) => (
              <optgroup key={section.key} label={section.label}>
                {RESOURCES.filter((r) => r.section === section.key).map((r) => (
                  <option key={r.key} value={r.key} className="bg-ocean-800">{r.label}</option>
                ))}
              </optgroup>
            ))}
          </select>
        </div>

        <div className="mb-3">
          <div className="flex items-center gap-1.5 text-[11px] text-slatey">
            <span>Admin</span><ChevronRight size={11} /><span>{active.group}</span>
            <ChevronRight size={11} /><span className="text-ink">{active.label}</span>
          </div>
          <h1 className="mt-0.5 text-xl font-semibold">{active.label}</h1>
          <p className="text-sm text-slatey">
            {active.readOnly ? "Read-only" : writable ? "Full create / read / update / delete" : "Read access"} ·
            {active.section === "platform" ? " platform-scoped" : " tenant-scoped"} · every change is audit-logged.
          </p>
        </div>
        {allTenants && active.section === "tenant" && (
          <div className="mt-2 rounded-lg border border-aurora-cyan/25 bg-aurora-cyan/5 px-3 py-1.5 text-xs text-slatey">
            Viewing <span className="text-aurora-cyan">all tenants</span> (read-only). Select a specific tenant
            in the ORG menu to create or edit records here.
          </div>
        )}

        <ResourceTable
          key={active.key}
          config={active}
          onView={setViewing}
          onEdit={(r) => setEditing(r)}
          onCreate={() => setEditing(null)}
        />
      </div>

      {viewing && (
        <ResourceDetail
          config={active}
          record={viewing}
          onClose={() => setViewing(null)}
          onEdit={(r) => { setViewing(null); setEditing(r); }}
        />
      )}
      {editing !== undefined && (
        <ResourceForm config={active} record={editing} onClose={() => setEditing(undefined)} />
      )}
    </div>
  );
}
